/**
 * 📦 ITEMS SYSTEM - UNIFIED EXPORTS
 *
 * All non-equipment items organized by type:
 * - Consumables (potions, food, buffs, scrolls, cubes, gemstones)
 * - Mob Drops (crafting materials by rarity)
 * - Lootboxes (dungeon chests with tiered loot)
 */

// Import from consumables
import {
  ITEM_CATEGORIES,
  HEALTH_POTIONS,
  BUFF_POTIONS,
  FOOD_ITEMS,
  BUFF_ITEMS,
  GEMSTONE_ITEMS,
  CUBE_ITEMS,
  MISC_ITEMS,
  QUEST_ITEMS,
  CONSUMABLES,
  ALL_CONSUMABLES,
} from './consumables';

// Import from mob drops
import {
  COMMON_MATERIALS,
  UNCOMMON_MATERIALS,
  RARE_MATERIALS,
  EPIC_MATERIALS,
  LEGENDARY_MATERIALS,
  MOB_DROPS,
  DUNGEON_RESOURCES,
  DUNGEON_DROP_TABLES,
  rollDungeonLoot,
  getDungeonResource,
  getAllDungeonResources,
  getDungeonResourcesByRarity,
} from './mobDrops';

// Import from lootboxes
import {
  LOOTBOXES,
  getChestForTier,
  getAllLootboxIds,
  isLootbox,
  getLootbox,
} from './lootboxes';

// Import from boss chests
import {
  BOSS_CHESTS,
  getBossChest,
  getAllBossChestIds,
  isBossChest,
  createBossChestInstance,
} from './bossChests';

// Re-export everything from consumables
export {
  ITEM_CATEGORIES,
  HEALTH_POTIONS,
  BUFF_POTIONS,
  FOOD_ITEMS,
  BUFF_ITEMS,
  GEMSTONE_ITEMS,
  CUBE_ITEMS,
  MISC_ITEMS,
  QUEST_ITEMS,
  CONSUMABLES,
  ALL_CONSUMABLES,
} from './consumables';

// Re-export everything from mob drops
export {
  COMMON_MATERIALS,
  UNCOMMON_MATERIALS,
  RARE_MATERIALS,
  EPIC_MATERIALS,
  LEGENDARY_MATERIALS,
  MOB_DROPS,
  DUNGEON_RESOURCES,
  DUNGEON_DROP_TABLES,
  rollDungeonLoot,
  getDungeonResource,
  getAllDungeonResources,
  getDungeonResourcesByRarity,
} from './mobDrops';

// Re-export everything from lootboxes (use import then export to ensure webpack compatibility)
export {
  LOOTBOXES,
  getChestForTier,
  getAllLootboxIds,
  isLootbox,
  getLootbox,
};

// Re-export boss chests
export {
  BOSS_CHESTS,
  getBossChest,
  getAllBossChestIds,
  isBossChest,
  createBossChestInstance,
};

// ============================================================
// MATERIALS (Re-export from DUNGEON_RESOURCES with item metadata)
// This maintains backwards compatibility with the old items.js
// ============================================================
export const MATERIALS = Object.entries(DUNGEON_RESOURCES).reduce((acc, [id, resource]) => {
  acc[id] = {
    ...resource,
    category: ITEM_CATEGORIES.MATERIAL,
    usable: false,
  };
  return acc;
}, {});

// ============================================================
// ALL ITEMS REGISTRY (Unified)
// ============================================================
export const ALL_ITEMS = {
  ...CONSUMABLES,
  ...MATERIALS,
  ...GEMSTONE_ITEMS,
  ...BUFF_ITEMS,
  ...QUEST_ITEMS,
  ...MISC_ITEMS,
  ...CUBE_ITEMS,
  ...LOOTBOXES,
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get items by category
 */
export function getItemsByCategory(category) {
  return Object.values(ALL_ITEMS).filter(item => item.category === category);
}

/**
 * Get items by rarity
 */
export function getItemsByRarity(rarity) {
  return Object.values(ALL_ITEMS).filter(item => item.rarity === rarity);
}

/**
 * Get item by ID
 */
export function getItem(itemId) {
  return ALL_ITEMS[itemId] || null;
}

/**
 * Check if item is usable
 */
export function isUsableItem(itemId) {
  const item = getItem(itemId);
  return item?.usable === true;
}

/**
 * Get item categories
 */
export function getCategories() {
  return Object.values(ITEM_CATEGORIES);
}

/**
 * Get total item count
 */
export function getTotalItemCount() {
  return Object.keys(ALL_ITEMS).length;
}

/**
 * Get item count by category
 */
export function getItemCountByCategory() {
  return Object.values(ITEM_CATEGORIES).reduce((acc, category) => {
    acc[category] = getItemsByCategory(category).length;
    return acc;
  }, {});
}
